import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

import { Icon, Wizard, WizardStep } from '@factorialco/deprecated-design-system'
import { F0Button as Button } from '@factorialco/f0-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  PerDiemWizardProvider,
  usePerDiemWizard,
} from 'modules/expenses/components/expenses/New/PerDiemWizard/form/context'
import { PerDiemWizardData } from 'modules/expenses/components/expenses/New/PerDiemWizard/form/types'
import { useCreateGermanyPerDiem } from 'modules/expenses/components/expenses/New/PerDiemWizard/hooks/useCreateGermanyPerDiem'
import { useSendGermanyPerDiemForReview } from 'modules/expenses/components/expenses/New/PerDiemWizard/hooks/useSendGermanyPerDiemForReview'
import { useExpensesNavigation } from 'modules/expenses/hooks/useExpensesNavigation'

import CompanyPaidItems from './_CompanyPaidItems'
import ReviewAndSubmit from './_ReviewAndSubmit'
import TripDetails from './_TripDetails'

const formatCurrency = (amountCents: number, currency: string): string => {
  const amount = amountCents / 100
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
  }).format(amount)
}

interface PerDiemWizardModalProps {
  onClose: () => void
}

interface PerDiemWizardModalContentProps {
  onClose: () => void
  locked?: boolean
}

const hasUnsavedChanges = (data: PerDiemWizardData): boolean => {
  if (data.startDate || data.endDate) return true
  if (data.destinations.some((dest) => dest.locationData.country)) return true
  if (data.description) return true
  if (data.internalReference) return true
  if (data.files.length > 0) return true
  if (data.dayExclusions.length > 0) return true
  return false
}

const PerDiemWizardModalContentWithLock = ({ onClose, locked }: PerDiemWizardModalContentProps) => {
  const { data, calculation } = usePerDiemWizard()
  const { employee, company } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useExpensesNavigation()
  const { createPerDiem, isCreating } = useCreateGermanyPerDiem()
  const { sendPerDiemForReview, isSending } = useSendGermanyPerDiemForReview()
  const [showExitConfirmation, setShowExitConfirmation] = useState(false)
  const isDirty = hasUnsavedChanges(data)

  const initialData: PerDiemWizardData = data

  const footerMessage =
    calculation.totalAmountCents > 0
      ? `${formatCurrency(calculation.totalAmountCents, calculation.currency)} ${i18n.t(
          'expenses_v2.forms.germany_per_diem_modal.per_diem_total'
        )}`
      : calculation.isCalculating
      ? i18n.t('common.loading')
      : ''

  const hasValidDestination = data.destinations.some((dest) => dest.locationData.country)

  const handleSaveAsDraft = async (wizardData: PerDiemWizardData) => {
    if (!company?.id) {
      return
    }

    const result = await createPerDiem(wizardData, company.id)

    if (result.success && result.expensableId) {
      navigate({
        pathname: navigation.helpers.expensableDetail(result.expensableId),
      })
      onClose()
    } else if (result.error) {
      app.setFlash({
        type: 'wrong',
        message: result.error,
        time: 3000,
      })
    }
  }

  const handleSubmit = async () => {
    if (!company?.id) {
      return
    }

    const result = await sendPerDiemForReview(data, company.id)

    if (result.success && result.expensableId) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('expenses_v2.modals.new_expense.submitted_for_review_successfully'),
      })
      navigate({
        pathname: navigation.helpers.expensableDetail(result.expensableId),
      })
      onClose()
    } else if (result.error) {
      app.setFlash({
        type: 'wrong',
        message: 'There was an error submitting your expense',
        time: 3000,
      })
    }
  }

  const secondaryActionWithLabel = {
    action: () => {
      // Use context data instead of wizardData since our steps update the context
      handleSaveAsDraft(data)
    },
    label: i18n.t('expenses_v2.buttons.save_as_draft'),
  }

  const tripDetailsStep: WizardStep<PerDiemWizardData> = {
    title: i18n.t('expenses_v2.forms.germany_per_diem_modal.steps.step1.title'),
    nextText: i18n.t('common.next'),
    render: () => <TripDetails />,
    isCompleted: () => Boolean(data.startDate && data.endDate && hasValidDestination),
    footerMessage,
  }

  const companyPaidItemsStep: WizardStep<PerDiemWizardData> = {
    title: i18n.t('expenses_v2.forms.germany_per_diem_modal.steps.step2.title'),
    nextText: i18n.t('common.next'),
    render: () => <CompanyPaidItems />,
    isCompleted: () => true,
    footerMessage,
  }

  const reviewSubmitStep: WizardStep<PerDiemWizardData> = {
    title: i18n.t('expenses_v2.forms.germany_per_diem_modal.steps.step3.title'),
    nextText: i18n.t('common.submit'),
    secondaryAction: secondaryActionWithLabel,
    render: () => <ReviewAndSubmit />,
    isCompleted: () => true,
    footerMessage,
  }

  const handleClose = () => {
    if (hasUnsavedChanges(data)) {
      setShowExitConfirmation(true)
    } else {
      onClose()
    }
  }

  const handleDiscardAndClose = () => {
    setShowExitConfirmation(false)
    onClose()
  }

  // Handle ESC key to show confirmation when wizard is locked due to dirty state
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === 'Escape' &&
        isDirty &&
        !showExitConfirmation &&
        !locked &&
        !isCreating &&
        !isSending
      ) {
        setShowExitConfirmation(true)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isDirty, showExitConfirmation, locked, isCreating, isSending])

  return (
    <>
      <Wizard.Modal
        title={i18n.t('expenses_v2.forms.germany_per_diem_modal.title')}
        steps={[tripDetailsStep, companyPaidItemsStep, reviewSubmitStep]}
        initialData={initialData}
        onSubmit={handleSubmit}
        onClose={handleClose}
        size="large"
        locked={locked || isCreating || isSending || isDirty}
      />
      {isDirty &&
        !showExitConfirmation &&
        !locked &&
        !isCreating &&
        !isSending &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'min(1100px, 90vw)',
              height: 'min(850px, 90vh)',
              pointerEvents: 'none',
              zIndex: 999,
            }}
          >
            <button
              onClick={() => setShowExitConfirmation(true)}
              aria-label={i18n.t('common.close')}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                pointerEvents: 'auto',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon.Medium icon="cross" />
            </button>
          </div>,
          document.body
        )}
      {showExitConfirmation &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
            }}
          >
            {/* Backdrop */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
              }}
              onClick={() => setShowExitConfirmation(false)}
            />
            {/* Confirmation dialog */}
            <div
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="exit-confirmation-title"
              aria-describedby="exit-confirmation-desc"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: '#fff',
                borderRadius: 12,
                padding: 24,
                width: 400,
                maxWidth: '90vw',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.16)',
                display: 'flex',
                flexDirection: 'column' as const,
                gap: 24,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon.Medium icon="alert-circle-line" color="tangerine1000" />
                  <span
                    id="exit-confirmation-title"
                    style={{ fontSize: 16, fontWeight: 600, lineHeight: '24px' }}
                  >
                    {i18n.t('expenses_v2.forms.germany_per_diem_modal.exit_confirmation.title')}
                  </span>
                </div>
                <p
                  id="exit-confirmation-desc"
                  style={{ margin: 0, fontSize: 14, lineHeight: '20px', color: '#6F6D78' }}
                >
                  {i18n.t('expenses_v2.forms.germany_per_diem_modal.exit_confirmation.description')}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button
                  variant="outline"
                  label={i18n.t('common.cancel')}
                  onClick={() => setShowExitConfirmation(false)}
                />
                <Button
                  variant="critical"
                  label={i18n.t(
                    'expenses_v2.forms.germany_per_diem_modal.exit_confirmation.discard'
                  )}
                  onClick={handleDiscardAndClose}
                />
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  )
}

export const PerDiemWizardModal = ({ onClose }: PerDiemWizardModalProps) => {
  // Lock the modal initially to prevent auto-close from stale events
  // This is a workaround for a known Wizard.Modal bug
  const [locked, setLocked] = useState(true)

  useEffect(() => {
    // Unlock after mount to allow normal close behavior
    const timer = setTimeout(() => {
      setLocked(false)
    }, 200)
    return () => clearTimeout(timer)
  }, [])

  return (
    <PerDiemWizardProvider>
      <PerDiemWizardModalContentWithLock onClose={onClose} locked={locked} />
    </PerDiemWizardProvider>
  )
}
